// Copyright (c) 2025-2026 Motswagae Holding (Pty) Ltd. All Rights Reserved.
// CIPC Registration: 2019/443098/07
// Author: Velly Sipho Phaloane
//
// This source code is the proprietary and confidential property of
// Motswagae Holding (Pty) Ltd, trading as 360Wallet.
// Unauthorized copying, modification, distribution, or use of this
// software, in whole or in part, is strictly prohibited.
//
// Protected under the Copyright Act 98 of 1978 (South Africa)
// and international copyright treaties.
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Separator } from "../components/ui/separator";
import { Badge } from "../components/ui/badge";
import { getSettlementAccount, createSettlementAccount, updateSettlementAccount } from "../lib/api";
import { toast } from "sonner";
import { 
  Building2,
  Save, 
  CheckCircle2, 
  AlertCircle,
  Wallet,
  ArrowDownToLine,
  RefreshCw
} from "lucide-react";

const BANK_OPTIONS = [
  { value: "ABSA", label: "ABSA Bank" },
  { value: "Nedbank", label: "Nedbank" },
  { value: "Standard Bank", label: "Standard Bank" },
  { value: "FNB", label: "First National Bank" },
  { value: "Capitec", label: "Capitec Bank" },
  { value: "Other", label: "Other" }
];

const ACCOUNT_TYPE_OPTIONS = [
  { value: "Business Current", label: "Business Current Account" },
  { value: "Business Savings", label: "Business Savings Account" }
];

const SettlementAccount = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [account, setAccount] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  
  const [formData, setFormData] = useState({
    account_holder_name: "Motswagae Holding (Pty) Ltd",
    bank_name: "",
    account_type: "Business Current",
    account_number: "",
    branch_code: "",
    swift_bic: "",
    settlement_currency: "ZAR",
    is_active: true
  });

  useEffect(() => {
    fetchAccount();
  }, []);

  const fetchAccount = async () => {
    try {
      const response = await getSettlementAccount();
      if (response.data) {
        setAccount(response.data);
        setFormData({
          account_holder_name: response.data.account_holder_name || "Motswagae Holding (Pty) Ltd",
          bank_name: response.data.bank_name || "",
          account_type: response.data.account_type || "Business Current",
          account_number: response.data.account_number || "",
          branch_code: response.data.branch_code || "",
          swift_bic: response.data.swift_bic || "",
          settlement_currency: response.data.settlement_currency || "ZAR",
          is_active: response.data.is_active ?? true
        });
      }
    } catch (error) {
      // No account exists yet - that's okay
      console.log("No settlement account configured yet");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.bank_name) {
      toast.error("Please select a bank");
      return;
    }
    if (!formData.account_number) {
      toast.error("Please enter account number");
      return;
    }
    if (!formData.branch_code) {
      toast.error("Please enter branch code");
      return;
    }

    setSaving(true);
    try {
      if (account) {
        await updateSettlementAccount(formData);
        toast.success("Settlement account updated");
      } else {
        await createSettlementAccount(formData);
        toast.success("Settlement account created");
      }
      setHasChanges(false);
      await fetchAccount();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save settlement account");
    } finally {
      setSaving(false);
    }
  };

  const maskAccountNumber = (num) => {
    if (!num || num.length < 4) return num;
    return `****${num.slice(-4)}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settlement-account-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl">Settlement Account</h1>
          <p className="text-muted-foreground">Platform beneficiary banking details for fee settlements</p>
        </div>
        {account && (
          <Badge 
            variant="outline" 
            className={account.is_active ? "text-green-400 border-green-500/30" : "text-gray-400"}
          >
            {account.is_active ? (
              <><CheckCircle2 className="h-3 w-3 mr-1" /> Active</>
            ) : (
              <><AlertCircle className="h-3 w-3 mr-1" /> Inactive</>
            )}
          </Badge>
        )}
      </div>

      {/* Fee Flow Diagram */}
      <Card className="bg-secondary/30 border-border">
        <CardContent className="p-6">
          <h3 className="font-heading font-semibold mb-4">Settlement Flow</h3>
          <div className="flex items-center justify-center gap-4 flex-wrap text-sm">
            <div className="text-center p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <Building2 className="h-6 w-6 text-blue-400 mx-auto mb-1" />
              <div className="font-medium">Participating Institutions</div>
              <div className="text-xs text-muted-foreground">Banks, Retailers, ATM Operators</div>
            </div>
            <ArrowDownToLine className="h-5 w-5 text-muted-foreground rotate-[-90deg]" />
            <div className="text-center p-3 rounded-lg bg-primary/10 border border-primary/20">
              <Wallet className="h-6 w-6 text-primary mx-auto mb-1" />
              <div className="font-medium">360Wallet Platform</div>
              <div className="text-xs text-muted-foreground">Fee Calculated</div>
            </div>
            <ArrowDownToLine className="h-5 w-5 text-muted-foreground rotate-[-90deg]" />
            <div className="text-center p-3 rounded-lg bg-green-500/10 border border-green-500/20">
              <Building2 className="h-6 w-6 text-green-400 mx-auto mb-1" />
              <div className="font-medium">Motswagae Holding</div>
              <div className="text-xs text-muted-foreground">Settlement Account</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form */}
        <Card className="bg-card border-border lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              Beneficiary Details
            </CardTitle>
            <CardDescription>
              Banking details where all B2B transaction fee settlements are deposited
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Account Holder */}
              <div className="space-y-2">
                <Label htmlFor="account_holder_name">Account Holder Name</Label>
                <Input
                  id="account_holder_name"
                  value={formData.account_holder_name}
                  onChange={(e) => handleChange("account_holder_name", e.target.value)}
                  placeholder="Motswagae Holding (Pty) Ltd"
                  data-testid="account-holder-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Bank Name */}
                <div className="space-y-2">
                  <Label>Bank Name</Label>
                  <Select 
                    value={formData.bank_name} 
                    onValueChange={(value) => handleChange("bank_name", value)}
                  >
                    <SelectTrigger data-testid="bank-name-select">
                      <SelectValue placeholder="Select bank..." />
                    </SelectTrigger>
                    <SelectContent>
                      {BANK_OPTIONS.map((bank) => (
                        <SelectItem key={bank.value} value={bank.value}>
                          {bank.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Account Type */}
                <div className="space-y-2">
                  <Label>Account Type</Label>
                  <Select 
                    value={formData.account_type} 
                    onValueChange={(value) => handleChange("account_type", value)}
                  >
                    <SelectTrigger data-testid="account-type-select">
                      <SelectValue placeholder="Select account type..." />
                    </SelectTrigger>
                    <SelectContent>
                      {ACCOUNT_TYPE_OPTIONS.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Account Number */}
                <div className="space-y-2">
                  <Label htmlFor="account_number">Account Number</Label>
                  <Input
                    id="account_number"
                    value={formData.account_number}
                    onChange={(e) => handleChange("account_number", e.target.value)}
                    placeholder="Enter account number"
                    data-testid="account-number-input"
                  />
                </div>

                {/* Branch Code */}
                <div className="space-y-2">
                  <Label htmlFor="branch_code">Branch Code</Label>
                  <Input
                    id="branch_code"
                    value={formData.branch_code}
                    onChange={(e) => handleChange("branch_code", e.target.value)}
                    placeholder="e.g., 632005"
                    data-testid="branch-code-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* SWIFT/BIC */}
                <div className="space-y-2">
                  <Label htmlFor="swift_bic">SWIFT/BIC Code (Optional)</Label>
                  <Input
                    id="swift_bic"
                    value={formData.swift_bic}
                    onChange={(e) => handleChange("swift_bic", e.target.value)}
                    placeholder="e.g., ABSAZAJJ"
                    data-testid="swift-input"
                  />
                </div>

                {/* Settlement Currency */}
                <div className="space-y-2">
                  <Label htmlFor="settlement_currency">Settlement Currency</Label>
                  <Input
                    id="settlement_currency"
                    value={formData.settlement_currency}
                    onChange={(e) => handleChange("settlement_currency", e.target.value)}
                    placeholder="ZAR"
                    data-testid="currency-input"
                  />
                </div>
              </div>

              <Separator />

              {/* Active Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <Label>Account Active</Label>
                  <p className="text-sm text-muted-foreground">
                    Enable this account for receiving settlements
                  </p>
                </div>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => handleChange("is_active", checked)}
                  data-testid="active-toggle"
                />
              </div>

              {/* Submit Button */}
              <Button 
                type="submit" 
                className="w-full shadow-[0_0_15px_rgba(59,130,246,0.3)]"
                disabled={saving || !hasChanges}
                data-testid="save-account-btn"
              >
                {saving ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {account ? "Update Settlement Account" : "Create Settlement Account"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Account Summary */}
        <div className="space-y-6">
          {account ? (
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-400" />
                  Account Configured
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Holder</span>
                    <span className="font-medium">{account.account_holder_name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Bank</span>
                    <span className="font-medium">{account.bank_name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Type</span>
                    <span className="font-medium">{account.account_type}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Account</span>
                    <span className="font-mono">{maskAccountNumber(account.account_number)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Branch</span>
                    <span className="font-mono">{account.branch_code}</span>
                  </div>
                  {account.swift_bic && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">SWIFT</span>
                      <span className="font-mono">{account.swift_bic}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Currency</span>
                    <span className="font-medium">{account.settlement_currency}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-card border-border">
              <CardContent className="p-6 text-center">
                <AlertCircle className="h-12 w-12 text-yellow-400 mx-auto mb-4" />
                <h3 className="font-heading font-semibold mb-2">No Account Configured</h3>
                <p className="text-sm text-muted-foreground">
                  Configure your settlement account to receive platform fees from B2B transactions.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Integration Info */}
          <Card className="bg-secondary/30 border-border">
            <CardHeader>
              <CardTitle className="font-heading text-sm">Automatic Integration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-muted-foreground">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-400 mt-0.5 shrink-0" />
                <span>Billing engine references this account</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-400 mt-0.5 shrink-0" />
                <span>Invoice generation includes account details</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-400 mt-0.5 shrink-0" />
                <span>Settlement reports use this as destination</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-400 mt-0.5 shrink-0" />
                <span>All changes logged to audit trail</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SettlementAccount;
